import { Component, Input, ViewChild, inject } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import {
  ZiroSelectComponent,
  ZiroSelectConfig,
  ZiroSelectOption,
} from '../../../../forms/fields/select/ziro-select.component';
import { Observable } from 'rxjs';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { map } from 'rxjs/operators';
import { DirectoryNumberResult } from '../../../models/generated/ziroModels';
import { SearchDirectoryNumberResource } from '../../../../self-serve/resources/search/search-directory-number.resource';
import {
  ExpressionEditorKeywordSearchEntity,
  ExpressionEditorKeywordSearchFormData,
} from '../call-logs-query-editor-keyword.component';

@Component({
  selector: 'ziro-expression-editor-keyword-extension-search',
  templateUrl: './expression-editor-keyword-extension-search.component.html',
  standalone: false,
})
export class ExpressionEditorKeywordExtensionSearchComponent extends ZiroFormAbstractDirective<
  ExpressionEditorKeywordSearchEntity,
  ExpressionEditorKeywordSearchFormData
> {
  protected _ziroFormStateService: ZiroFormStateService;
  private searchDirectoryNumberResource = inject(SearchDirectoryNumberResource);

  // TODO: Skipped for migration because:
  //  This input is inherited from a superclass, but the parent cannot be migrated.
  @Input() entity: ExpressionEditorKeywordSearchEntity;
  @ViewChild(ZiroSelectComponent) selectField: ZiroSelectComponent;

  formConfig = {
    fields: {
      'keyword-search': {
        dataAutomation: 'extension-select',
        componentConfig: new ZiroSelectConfig({
          asyncOptionsFn: this.searchExtension.bind(this),
          placeholder: 'tkey;reports.call_logs.keyword.search.extension',
          clearWithInput: true,
          hideDropdownArrow: true,
          minSearchLength: 3,
          hideClear: true,
          keepSearchInput: true,
        }),
      },
    },
    options: {
      columnClasses: {
        label: 'd-none',
        input: 'col-12',
      },
    },
  } as ZiroFormConfig;

  searchExtension(q: string): Observable<ZiroSelectOption[]> {
    return this.searchDirectoryNumberResource
      .get({
        q,
      })
      .pipe(
        map((results: DirectoryNumberResult[]) => {
          const directoryNumbers: ZiroSelectOption[] = results.map((result: DirectoryNumberResult) => {
            return {
              label: result.ref.extension,
              value: result.ref.extension,
            };
          });
          const uniqueDirectoryNumbers: ZiroSelectOption[] = Array.from(
            new Map(
              directoryNumbers.map((directoryNumber: ZiroSelectOption) => [directoryNumber.label, directoryNumber]),
            ).values(),
          );
          return uniqueDirectoryNumbers.sort(
            (result1: { label: string; value: string }, result2: { label: string; value: string }) => {
              return result1.label.localeCompare(result2.label);
            },
          );
        }),
      );
  }

  protected submit(): Observable<any> {
    return null;
  }

  toFormData = (entity: ExpressionEditorKeywordSearchEntity): ExpressionEditorKeywordSearchFormData => {
    return {
      'keyword-search': {
        label: entity.extensionUsernameOrDeviceName.value,
        value: entity.extensionUsernameOrDeviceName.value,
      },
    } as ExpressionEditorKeywordSearchFormData;
  };

  toEntity = (formData: ExpressionEditorKeywordSearchFormData): ExpressionEditorKeywordSearchEntity => {
    return {
      extensionUsernameOrDeviceName: { selection: 'extension', value: formData['keyword-search'].value },
    } as ExpressionEditorKeywordSearchEntity;
  };

  handleSelectClosed(): void {
    if (typeof this.selectField.fieldData === 'string' && this.selectField.fieldData.length >= 3) {
      // Nothing was selected but we received a search term and we'd like to use it.
      this.ziroFormsUpdateSource.next({
        valid: ZiroFormsValidationState.VALID,
        old: {
          ...this.entity,
        },
        new: {
          extensionUsernameOrDeviceName: {
            selection: 'extension',
            value: this.selectField.fieldData,
          },
        },
      });
    }
  }
}
